import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import {
  BookOpenText,
  CalendarBlank,
  ChartBar,
  CloudArrowDown,
  Coins,
  Gauge,
  Lightbulb,
  Medal,
  Lightning,
  SignOut,
  StarFour,
  UserMinus,
  UsersThree
} from "@phosphor-icons/react";
import { OverviewCommandDeck } from "./components/OverviewCommandDeck";
import { isWindLoginSurface, LoginPage } from "./components/LoginPage";
import { activeScopeTheme, applyCollectionPayload, applyLocalDemoData, applyPortalState, collectionMeta, dashboardMetrics, isLocalDemo, notifyCollectionDataChanged, registerScopes, runtimeScope } from "./data";
import { getAllianceSlug, getSessionInfo, setSessionInfo } from "./apiBase";

// 3D 星流场景（three.js，约 1.1MB）已随三国主题下线：hero 现在是一张宣纸横幅，
// 不再需要 WebGL，也省掉这块包体。
const MembersPage = lazy(() => import("./components/ManagementPages").then((module) => ({ default: module.MembersPage })));
const MeritPage = lazy(() => import("./components/ManagementPages").then((module) => ({ default: module.MeritPage })));
const DepartedPage = lazy(() => import("./components/ManagementPages").then((module) => ({ default: module.DepartedPage })));
const ActivitiesPage = lazy(() => import("./components/ManagementPages").then((module) => ({ default: module.ActivitiesPage })));
const FinancePage = lazy(() => import("./components/ManagementPages").then((module) => ({ default: module.FinancePage })));
const GuidesPage = lazy(() => import("./components/ManagementPages").then((module) => ({ default: module.GuidesPage })));
const TipsPage = lazy(() => import("./components/ManagementPages").then((module) => ({ default: module.TipsPage })));
const CollectionCenterPage = lazy(() => import("./components/CollectionCenterPage").then((module) => ({ default: module.CollectionCenterPage })));

const modules = [
  { id: "overview", label: "概览", icon: Gauge },
  { id: "members", label: "成员组织", icon: UsersThree },
  { id: "merit", label: "军功计算", icon: Medal },
  { id: "departed", label: "已离盟", icon: UserMinus },
  // 数据排行已并入成员组织（页内"名单/排行"切换）；这里保留 id 只为老链接 #/rankings 还能打开，
  // navHidden 让它不出现在导航条上。
  { id: "rankings", label: "数据排行", icon: ChartBar, navHidden: true },
  { id: "activities", label: "活动运营", icon: CalendarBlank },
  { id: "finance", label: "财务管理", icon: Coins },
  { id: "guides", label: "攻略内容", icon: BookOpenText },
  { id: "tips", label: "今日 Tips", icon: Lightbulb },
  { id: "collection", label: "采集中心", icon: CloudArrowDown }
] as const;

/** 手机触屏端底部 9 栏掌上作战导航 */
const mobileModules = [
  { id: "overview", label: "大营", icon: Gauge },
  { id: "members", label: "名谱", icon: UsersThree },
  { id: "merit", label: "军功", icon: Medal },
  { id: "departed", label: "离盟", icon: UserMinus },
  { id: "activities", label: "战令", icon: CalendarBlank },
  { id: "finance", label: "府库", icon: Coins },
  { id: "guides", label: "兵书", icon: BookOpenText },
  { id: "tips", label: "锦囊", icon: Lightbulb },
  { id: "collection", label: "调度", icon: CloudArrowDown }
] as const;

const metricIcons = [UsersThree, Lightning, ChartBar, StarFour] as const;

type ModuleId = (typeof modules)[number]["id"];

function routeFromHash(): ModuleId {
  const candidate = window.location.hash.replace(/^#\/?/, "").split("?")[0] as ModuleId;
  return modules.some((item) => item.id === candidate) ? candidate : "overview";
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(media.matches);
    sync(); media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);
  return reduced;
}

function OverviewPage({ reducedMotion }: { reducedMotion: boolean }) {
  const [compactViewport, setCompactViewport] = useState(() => window.matchMedia("(max-width: 760px)").matches);
  const [sceneMode, setSceneMode] = useState<"cinematic" | "calm">(() => {
    const runtime = navigator as Navigator & { deviceMemory?: number; connection?: { saveData?: boolean } };
    return reducedMotion || window.matchMedia("(max-width: 760px)").matches || runtime.connection?.saveData || (runtime.deviceMemory ?? 8) <= 4 || navigator.hardwareConcurrency <= 4 || window.matchMedia("(pointer: coarse)").matches ? "calm" : "cinematic";
  });
  const [sceneReady, setSceneReady] = useState(false);
  const sceneIsCalm = reducedMotion || sceneMode === "calm";
  const metrics = dashboardMetrics();
  useEffect(() => {
    const media = window.matchMedia("(max-width: 760px)");
    const sync = () => setCompactViewport(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);
  useEffect(() => {
    if (reducedMotion || compactViewport) {
      setSceneReady(false);
      return;
    }
    const runtime = window as Window & typeof globalThis & { requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void };
    if (runtime.requestIdleCallback) {
      const id = runtime.requestIdleCallback(() => setSceneReady(true), { timeout: 1000 });
      return () => runtime.cancelIdleCallback?.(id);
    }
    const timer = window.setTimeout(() => setSceneReady(true), 180);
    return () => window.clearTimeout(timer);
  }, [compactViewport, reducedMotion]);
  return (
    <>
      {/* 顶部不再留装饰封面带：顶栏和指标带之间空一条 60px 的纸带没有意义，
          指标数据直接跟在顶栏下面。 */}
      <OverviewCommandDeck>
        <div className="metric-grid" aria-label="核心数据">
          {metrics.map(({ label, value, suffix, compare, change }, index) => { const Icon = metricIcons[index]; return <article className="metric-card" key={label}><div className="metric-label"><Icon weight="duotone" /><span>{label}</span></div><p className="metric-value">{value}{suffix && <small>{suffix}</small>}</p><p className="metric-change">{compare}<strong>{change}</strong></p></article>; })}
        </div>
      </OverviewCommandDeck>
    </>
  );
}

function ActivePage({ route, reducedMotion }: { route: ModuleId; reducedMotion: boolean }) {
  if (route === "overview") return <OverviewPage reducedMotion={reducedMotion} />;
  if (route === "members") return <MembersPage />;
  // 数据排行已并入成员组织（名单页里用"总计/今日/本周"切换口径），老链接 #/rankings 落到名单页
  if (route === "rankings") return <MembersPage />;
  if (route === "merit") return <MeritPage />;
  if (route === "departed") return <DepartedPage />;
  if (route === "activities") return <ActivitiesPage />;
  if (route === "finance") return <FinancePage />;
  if (route === "guides") return <GuidesPage />;
  if (route === "tips") return <TipsPage />;
  return <CollectionCenterPage />;
}

const SAVE_LABELS: Record<string, string> = { activities: "活动", finance: "军费账本", guides: "攻略", tips: "今日 Tips" };

/** 保存结果的全局提示：写入成功 = 绿条；被服务端拒绝 = 红条常驻（必须点掉），
 *  绝不出现"看着像存上了其实没存"的情况。 */
function SaveFeedback() {
  const [state, setState] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    let timer: number | undefined;
    const onSave = (event: Event) => {
      const detail = (event as CustomEvent<{ key: string; ok: boolean; error?: string }>).detail || { key: "", ok: true };
      const label = SAVE_LABELS[detail.key] || detail.key;
      if (timer) window.clearTimeout(timer);
      if (detail.ok) {
        setState({ ok: true, text: `${label}已保存到云端` });
        timer = window.setTimeout(() => setState(null), 2500);
      } else {
        setState({ ok: false, text: `${label}保存失败：${detail.error || "未知错误"}。内容没有写入，请重试。` });
      }
    };
    window.addEventListener("alliance-portal-save", onSave);
    return () => { window.removeEventListener("alliance-portal-save", onSave); if (timer) window.clearTimeout(timer); };
  }, []);
  if (!state) return null;
  return (
    <div className={`save-feedback ${state.ok ? "is-ok" : "is-error"}`} role="status" aria-live="polite">
      <span>{state.text}</span>
      <button type="button" onClick={() => setState(null)} aria-label="关闭提示">×</button>
    </div>
  );
}

function ManagementApp() {
  const [activeModule, setActiveModule] = useState<ModuleId>(() => routeFromHash());
  const [dataState, setDataState] = useState<"loading" | "ready" | "error">("loading");
  const [dataError, setDataError] = useState("");
  const [signingOut, setSigningOut] = useState(false);
  const [sessionInfo, setSessionInfoState] = useState(() => getSessionInfo());
  const [, setCollectionVersion] = useState(0);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    document.documentElement.dataset.scope = runtimeScope;
    document.title = `${collectionMeta.scopeLabel}｜联盟管理中心`;
    document.documentElement.style.setProperty("--scope-accent", activeScopeTheme.accent);
    document.documentElement.style.setProperty("--scope-accent-strong", activeScopeTheme.accentStrong);
    document.documentElement.style.setProperty("--scope-accent-soft", activeScopeTheme.accentSoft);
    document.documentElement.style.setProperty("--scope-glow", activeScopeTheme.glow);
    return () => {
      delete document.documentElement.dataset.scope;
      document.documentElement.style.removeProperty("--scope-accent");
      document.documentElement.style.removeProperty("--scope-accent-strong");
      document.documentElement.style.removeProperty("--scope-accent-soft");
      document.documentElement.style.removeProperty("--scope-glow");
    };
  }, []);

  useEffect(() => {
    const onSnapshotRequested = (event: Event) => {
      const detail = (event as CustomEvent<{ payload?: unknown; release_id?: string }>).detail;
      if (!detail?.payload || typeof detail.payload !== "object") return;
      applyCollectionPayload(detail.payload as Parameters<typeof applyCollectionPayload>[0], detail.release_id || "");
      setCollectionVersion((value) => value + 1);
      notifyCollectionDataChanged();
    };
    window.addEventListener("alliance-collection-snapshot-requested", onSnapshotRequested);
    return () => window.removeEventListener("alliance-collection-snapshot-requested", onSnapshotRequested);
  }, []);

  async function signOut() {
    if (signingOut) return;
    setSigningOut(true);
    const next = "/qunxing/admin/#/overview";
    try {
      await fetch("/wuying/logout", {
        method: "GET",
        credentials: "include",
        cache: "no-store",
        redirect: "manual"
      });
    } finally {
      try { window.localStorage.removeItem("alliance:scope"); } catch { /* 忽略 */ }
      window.location.replace(`/qunxing/login/?next=${encodeURIComponent(next)}`);
    }
  }

  useEffect(() => {
    if (!window.location.hash) window.history.replaceState(null, "", "#/overview");
    const sync = () => { setActiveModule(routeFromHash()); window.scrollTo({ top: 0, behavior: reducedMotion ? "auto" : "smooth" }); };
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, [reducedMotion]);

  useEffect(() => {
    const nav = document.querySelector<HTMLElement>(".primary-nav");
    const active = nav?.querySelector<HTMLElement>(".nav-item.is-active");
    if (!nav || !active || window.innerWidth > 620) return;
    nav.scrollTo({
      left: Math.max(0, active.offsetLeft - (nav.clientWidth - active.clientWidth) / 2),
      behavior: reducedMotion ? "auto" : "smooth"
    });
  }, [activeModule, reducedMotion]);

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      if (isLocalDemo) {
        applyLocalDemoData();
        if (!cancelled) setDataState("ready");
        return;
      }
      const authResponse = await fetch("/wuying/api/admin/me", { credentials: "include", headers: { Accept: "application/json" } });
      if (authResponse.status === 401) {
        const next = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        window.location.assign(`/qunxing/login/?next=${encodeURIComponent(next)}`);
        return;
      }
      if (!authResponse.ok) throw new Error("管理账号权限校验失败");
      // 账号归属哪个分组，就自动落到哪个分组的管理页（URL 里带别的分组不作数）
      const account = await authResponse.json().catch(() => ({})) as { scope_id?: string; scopes?: Array<{ scope: string; label: string }>; group?: string; locked?: boolean; alliance_name?: string };
      // 按该账号所属联盟注册分组（御风是黄锋团/…，山河暂不分组），再解析 scope 标签
      registerScopes(account.scopes);
      // 存下来：分组筛选器 / 导出按钮按这个判断（locked = 群星分组长锁本组）
      setSessionInfo({
        scopes: Array.isArray(account.scopes) ? account.scopes : getSessionInfo().scopes,
        ownGroup: String(account.group || ""),
        ownScope: String(account.scope_id || "alliance"),
        locked: !!account.locked,
        allianceName: String(account.alliance_name || ""),
      });
      setSessionInfoState(getSessionInfo());
      if (account.scope_id && account.scope_id !== runtimeScope) {
        try { window.localStorage.setItem("alliance:scope", account.scope_id); } catch { /* 忽略 */ }
        window.location.replace("/qunxing/admin/");
        return;
      }
      const [response, ...portalResponses] = await Promise.all([
        fetch(`/alliance-system/api/public/alliances/${getAllianceSlug()}/scopes/${runtimeScope}/data`, { headers: { Accept: "application/json" } }),
        ...["activities", "finance", "guides", "tips"].map((key) => fetch(`/wuying/api/portal/scopes/${runtimeScope}/${key}`, { credentials: "include", headers: { Accept: "application/json" } }))
      ]);
      const result = await response.json();
      if (result.scope_id && result.scope_id !== runtimeScope) throw new Error("管理端收到的发布范围与当前分组不一致");
      if (!response.ok || !result.ok || !result.payload) throw new Error(result.error || "范围数据尚未发布");
      const [activities, finance, guides, tips] = await Promise.all(portalResponses.map(async (portalResponse) => {
        const payload = await portalResponse.json();
        if (!portalResponse.ok || !payload.ok || !Array.isArray(payload.value)) throw new Error(payload.error || "管理数据初始化失败");
        return payload.value;
      }));
      applyCollectionPayload(result.payload, result.release_id || "");
      applyPortalState({ activities, finance, guides, tips });
      if (!cancelled) setDataState("ready");
    }
    boot().catch((error) => { if (!cancelled) { setDataError(error instanceof Error ? error.message : "载入失败"); setDataState("error"); } });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (isLocalDemo || dataState !== "ready") return;
    let cancelled = false;
    let refreshing = false;
    const refreshLatestCollection = async () => {
      if (cancelled || refreshing || document.hidden || collectionMeta.selectedSnapshot !== "latest") return;
      refreshing = true;
      try {
        const response = await fetch(`/alliance-system/api/public/alliances/${getAllianceSlug()}/scopes/${runtimeScope}/data`, {
          headers: { Accept: "application/json" },
          cache: "no-store"
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.ok || !result.payload) return;
        if (result.scope_id && result.scope_id !== runtimeScope) return;
        const nextRunId = String(result.payload.collection?.run?.run_id || "");
        const nextReleaseId = String(result.release_id || "");
        if (nextRunId === collectionMeta.runId && nextReleaseId === collectionMeta.releaseId) return;
        applyCollectionPayload(result.payload, nextReleaseId);
        setCollectionVersion((value) => value + 1);
        notifyCollectionDataChanged();
      } finally {
        refreshing = false;
      }
    };
    const timer = window.setInterval(() => void refreshLatestCollection(), 10_000);
    const onVisibility = () => { if (!document.hidden) void refreshLatestCollection(); };
    const onRefreshRequested = () => void refreshLatestCollection();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("alliance-collection-latest-refresh-requested", onRefreshRequested);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("alliance-collection-latest-refresh-requested", onRefreshRequested);
    };
  }, [dataState]);

  useEffect(() => {
    if (isLocalDemo || dataState !== "ready" || typeof EventSource === "undefined") return;
    const source = new EventSource(`/wuying/api/portal/scopes/${runtimeScope}/stream`);
    const onRevision = (event: MessageEvent<string>) => {
      try {
        const detail = JSON.parse(event.data || "{}");
        window.dispatchEvent(new CustomEvent("alliance-portal-revision", { detail }));
      } catch {
        window.dispatchEvent(new Event("alliance-portal-revision"));
      }
    };
    source.addEventListener("revision", onRevision);
    return () => {
      source.removeEventListener("revision", onRevision);
      source.close();
    };
  }, [dataState]);

  const activeLabel = useMemo(() => modules.find((item) => item.id === activeModule)?.label ?? "概览", [activeModule]);

  if (dataState === "loading") return <div className="page-loading"><span className="status-dot" />正在核验账号并读取数据中心</div>;
  if (dataState === "error") return <div className="page-loading page-error-state"><span>数据中心载入失败：{dataError}</span><button className="session-logout-button is-prominent" type="button" disabled={signingOut} onClick={() => void signOut()}><SignOut weight="bold" /><span>{signingOut ? "正在退出" : "退出并重新登录"}</span></button></div>;
  return (
    <main className={`app-shell scope-${runtimeScope}`} data-scope={runtimeScope}>
      <SaveFeedback />
      <header className="topbar">
        <a className="brand" href="#/overview" aria-label="返回概览"><span className="brand-mark" aria-hidden="true">盟</span><span>{collectionMeta.allianceName}·{collectionMeta.scopeLabel}</span></a>
        <nav className="primary-nav" aria-label="联盟管理模块">{modules.filter((item) => !("navHidden" in item && item.navHidden)).map(({ id, label, icon: Icon }) => <a key={id} className={activeModule === id ? "nav-item is-active" : "nav-item"} href={`#/${id}`} aria-current={activeModule === id ? "page" : undefined}><Icon weight={activeModule === id ? "fill" : "regular"} /><span>{label}</span></a>)}</nav>
        <div className="session-actions">
          {!sessionInfo.locked && sessionInfo.scopes.length > 1 && (
            <label className="group-switcher" title="切换分组视图">
              <span>分组</span>
              <select
                value={runtimeScope}
                onChange={(event) => {
                  const next = event.target.value;
                  try { window.localStorage.setItem("alliance:scope", next); } catch { /* 忽略 */ }
                  window.location.reload();
                }}
              >
                {sessionInfo.scopes.map((s) => <option key={s.scope} value={s.scope}>{s.label}</option>)}
              </select>
            </label>
          )}
          {activeModule !== "overview" && <div className="topbar-context" title={`当前模块：${activeLabel}`}><span className="status-dot" /><span><b>无人值守待命</b><small>管理服务在线</small></span></div>}
          <button className="session-logout-button" type="button" disabled={signingOut} onClick={() => void signOut()} aria-label="退出管理中心登录"><SignOut weight="bold" /><span>{signingOut ? "退出中" : "退出登录"}</span></button>
        </div>
      </header>
      <Suspense fallback={<div className="page-loading"><span className="status-dot" />正在载入管理模块</div>}><ActivePage route={activeModule} reducedMotion={reducedMotion} /></Suspense>
      {/* 手机移动端专属：底部固定 9 栏掌上作战 Bar */}
      <nav className="mobile-bottom-bar" aria-label="掌上作战导航">
        {mobileModules.map(({ id, label, icon: Icon }) => (
          <a
            key={id}
            className={activeModule === id ? "mob-tab is-active" : "mob-tab"}
            href={`#/${id}`}
            aria-current={activeModule === id ? "page" : undefined}
          >
            <Icon weight={activeModule === id ? "fill" : "regular"} className="mob-tab-ico" />
            <span className="mob-tab-lbl">{label}</span>
          </a>
        ))}
      </nav>
    </main>
  );
}

export default function App() {
  return isWindLoginSurface() ? <LoginPage /> : <ManagementApp />;
}
